import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from './activity.adapter';

import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { HDInsightLinkedService } from 'app/model/linkedservice/hdinsight.linkedservice';
import { ReferenceAdapter } from '../reference.adapter';
import { toArgsArray, toArgsString } from 'app/model/adapters/activities/arguments-adapter.util';
import { toParameterClass, toParameterObject } from 'app/model/adapters/linkedservices/parameter-config.util';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import { ValueAdapter } from '../value.adapter';
import { FilePath } from 'app/model/dataset/file-path';
import _ from 'lodash';
import { linkedServiceReferenceWithoutParameterizationRemainer } from '../reference-remainer';
import { AzureHDInsightActivity } from 'app/model/pipeline/azure-hdinsight.activity';
import {
    AzureBlobStorageLinkedService,
    AzureSqlDatabaseLinkedService,
    Constants,
    stringIsNullOrEmpty,
    ModelConstants,
    Named
} from 'shell-models-lib';
import { HDInsightOnDemandLinkedService } from 'app/model/linkedservice/hdinsightondemand.linkedservice';
import { passwordOrAKVtoSecretBase } from '../linkedservices/azure-key-vault.util';
import {
    ActivityConnectionContext,
    IAzureHdInsightActivityContext
} from 'app/interfaces/copilot.pipeline.action.types';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ModelService } from 'app/model/services/model.service';
import { findLinkedServiceFromContext } from 'app/shared/utils/update-pipeline-skill.util';
import { IFrameService } from 'app/services/iframe.service';
import { getFabricLsForParameterizedConnections } from '../utils';
import { GlobalVariables } from 'app/services/globals';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureHDInsightActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: GlobalVariables.enableAllConnectionParamForExternalActivities && __IS_FABRIC_DPE__ ? '*' : linkedServiceReferenceWithoutParameterizationRemainer,
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            hdiActivityType: '*',
            scriptSettings: '*',
            sparkSettings: '*',
            clusterSettings: '*',
            jarSettings: '*',
            getDebugInfo: '*',
            arguments: '*',
            defines: '*',
            variables: '*',
            queryTimeout: '*'
        },
        externalReferences: '*'
    }
})
export class AzureHDInsightActivityAdapter extends ActivityAsyncAdapter<
    AzureHDInsightActivity,
    Model.AzureHDInsightActivity,
    IAzureHdInsightActivityContext
> {
    public model(): IModel {
        return Metamodel.modelFor(AzureHDInsightActivity);
    }

    public override async toFabricCopilotContext(
        activity: AzureHDInsightActivity
    ): Promise<IAzureHdInsightActivityContext> {
        if (activity) {
            const activityContext: IAzureHdInsightActivityContext = await super.toFabricCopilotContext(activity);
            activityContext.HdInsightType = activity.hdiActivityType as IAzureHdInsightActivityContext['HdInsightType'];
            activityContext.ScriptFilePath = ValueAdapter.toResource(
                activity,
                AzureHDInsightActivity.SCRIPT_PATH,
                true
            );
            if (activity.linkedService) {
                let lsAdapter;
                if (activity.linkedService instanceof HDInsightOnDemandLinkedService) {
                    lsAdapter = this.context.asyncAdapterFor<
                        HDInsightOnDemandLinkedService,
                        Model.LinkedServiceResource,
                        ActivityConnectionContext
                    >(Metamodel.modelByName(HDInsightOnDemandLinkedService.MODEL.name));
                    activityContext.Connection = await lsAdapter?.toFabricCopilotContext(activity.linkedService);
                } else {
                    lsAdapter = this.context.asyncAdapterFor<
                        HDInsightLinkedService,
                        Model.LinkedServiceResource,
                        ActivityConnectionContext
                    >(Metamodel.modelByName(HDInsightLinkedService.MODEL.name));
                    activityContext.Connection = await lsAdapter?.toFabricCopilotContext(activity.linkedService);
                }
            }
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IAzureHdInsightActivityContext,
        model?: AzureHDInsightActivity
    ): Promise<AzureHDInsightActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            ValueAdapter.toModel(model, AzureHDInsightActivity.SCRIPT_PATH, context.ScriptFilePath);
            model.hdiActivityType = context.HdInsightType ?? 'HDInsightHive';
            if (context.Connection) {
                const modelService = await getServiceInstance<ModelService>(this.context.contextData, 'ModelService');
                const iframeService = await getServiceInstance<IFrameService>(
                    this.context.contextData,
                    'IFrameService'
                );
                model.linkedService = (
                    await findLinkedServiceFromContext<HDInsightLinkedService>(context.Connection, {
                        modelService,
                        iframeService
                    })
                ).linkedService;
                if (model.linkedService) {
                    const lsAdapter = Metamodel.isModel(context.Connection.Type)
                        ? this.context.asyncAdapterFor<
                            LinkedService,
                            Model.LinkedServiceResource,
                            ActivityConnectionContext
                        >(Metamodel.modelByName(context.Connection.Type))
                        : undefined;
                    await lsAdapter?.fromFabricCopilotContext(
                        context.Connection,
                        model.linkedService as HDInsightOnDemandLinkedService
                    );
                }
            }
        }
        return model;
    }

    protected async _toResource(activity: AzureHDInsightActivity): Promise<Model.AzureHDInsightActivity> {
        const modelHDInsightActivity: Model.AzureHDInsightActivity = super._getCommonActivityProperties(
            activity,
            'AzureHDInsight'
        ) as Model.AzureHDInsightActivity;
        const azureHDInsightActivity: AzureHDInsightActivity = activity as AzureHDInsightActivity;

        const hdiActivityType = activity.hdiActivityType;
        if (hdiActivityType === 'HDInsightHive' || hdiActivityType === 'HDInsightPig') {
            modelHDInsightActivity.typeProperties = {
                arguments: toArgsArray(
                    azureHDInsightActivity.azureHDInsightActivityArguments,
                    azureHDInsightActivity,
                    AzureHDInsightActivity.ARGUMENTS
                ),
                getDebugInfo: azureHDInsightActivity.getDebugInfo,
                defines: toParameterObject(azureHDInsightActivity.defines),
                variables: toParameterObject(azureHDInsightActivity.variables),
                queryTimeout: azureHDInsightActivity.queryTimeout,
                hdiActivityType: azureHDInsightActivity.hdiActivityType
            };
            const scriptPath = ValueAdapter.toResource(azureHDInsightActivity, AzureHDInsightActivity.SCRIPT_PATH);
            modelHDInsightActivity.typeProperties.scriptSettings = {
                scriptPath: scriptPath.type ? scriptPath : scriptPath.path,
                fileConnection: azureHDInsightActivity.fileConnection
                    ? this._toLinkedServiceReferenceModel(azureHDInsightActivity.fileConnection)
                    : undefined
            };
        } else if (hdiActivityType === 'HDInsightMapReduce') {
            modelHDInsightActivity.typeProperties = {
                arguments: toArgsArray(
                    azureHDInsightActivity.azureHDInsightActivityArguments,
                    azureHDInsightActivity,
                    AzureHDInsightActivity.ARGUMENTS
                ),
                getDebugInfo: azureHDInsightActivity.getDebugInfo,
                defines: toParameterObject(azureHDInsightActivity.defines),
                hdiActivityType: azureHDInsightActivity.hdiActivityType
            };
            const jarFilePath = ValueAdapter.toResource(azureHDInsightActivity, AzureHDInsightActivity.JAR_FILE_PATH);
            modelHDInsightActivity.typeProperties.jarSettings = {
                className: ValueAdapter.toResource(azureHDInsightActivity, AzureHDInsightActivity.CLASS_NAME),
                jarFilePath: jarFilePath.type ? jarFilePath : jarFilePath.path,
                jarLibs: azureHDInsightActivity.jarLibs,
                fileConnection: azureHDInsightActivity.fileConnection
                    ? this._toLinkedServiceReferenceModel(azureHDInsightActivity.fileConnection)
                    : undefined
            };
        } else if (hdiActivityType === 'HDInsightSpark') {
            modelHDInsightActivity.typeProperties = {
                arguments: toArgsArray(
                    azureHDInsightActivity.azureHDInsightActivityArguments,
                    azureHDInsightActivity,
                    AzureHDInsightActivity.ARGUMENTS
                ),
                getDebugInfo: azureHDInsightActivity.getDebugInfo,
                hdiActivityType: azureHDInsightActivity.hdiActivityType
            };
            modelHDInsightActivity.typeProperties.sparkSettings = {
                rootPath: ValueAdapter.toResource(azureHDInsightActivity, AzureHDInsightActivity.ROOT_PATH),
                entryFilePath: ValueAdapter.toResource(azureHDInsightActivity, AzureHDInsightActivity.ENTRY_FILE_PATH),
                className: azureHDInsightActivity.configType === 'Jar' ? azureHDInsightActivity.className : undefined,
                proxyUser: ValueAdapter.toResource(azureHDInsightActivity, AzureHDInsightActivity.PROXY_USER),
                sparkConfig: toParameterObject(azureHDInsightActivity.sparkConfig),
                fileConnection: azureHDInsightActivity.fileConnection
                    ? this._toLinkedServiceReferenceModel(azureHDInsightActivity.fileConnection)
                    : undefined
            };
        } else if (hdiActivityType === 'HDInsightStreaming') {
            modelHDInsightActivity.typeProperties = {
                mapper: ValueAdapter.toResource(azureHDInsightActivity, AzureHDInsightActivity.MAPPER),
                reducer: ValueAdapter.toResource(azureHDInsightActivity, AzureHDInsightActivity.REDUCER),
                arguments: toArgsArray(
                    azureHDInsightActivity.azureHDInsightActivityArguments,
                    azureHDInsightActivity,
                    AzureHDInsightActivity.ARGUMENTS
                ),
                defines: toParameterObject(azureHDInsightActivity.defines),
                getDebugInfo: azureHDInsightActivity.getDebugInfo,
                hdiActivityType: azureHDInsightActivity.hdiActivityType
            };
            modelHDInsightActivity.typeProperties.fileSettings = {
                filePaths: [azureHDInsightActivity.mapperFilePath.path, azureHDInsightActivity.reducerFilePath.path],
                input: ValueAdapter.toResource(azureHDInsightActivity, AzureHDInsightActivity.INPUT),
                output: ValueAdapter.toResource(azureHDInsightActivity, AzureHDInsightActivity.OUTPUT),
                fileConnection: azureHDInsightActivity.fileConnection
                    ? this._toLinkedServiceReferenceModel(azureHDInsightActivity.fileConnection)
                    : undefined
            };
        }

        if (azureHDInsightActivity.linkedService) {
            modelHDInsightActivity.linkedServiceName = this._toLinkedServiceReferenceModel(
                azureHDInsightActivity.linkedService
            );
            if (
                __IS_FABRIC_DPE__ &&
                azureHDInsightActivity.linkedServiceType != null &&
                Named.OBJECT_ID.templateValue(azureHDInsightActivity.linkedService)
            ) {
                modelHDInsightActivity.linkedServiceName.referenceType =
                    azureHDInsightActivity.linkedServiceType;
            }
        }

        if (modelHDInsightActivity.typeProperties != null) {
            if (
                azureHDInsightActivity.linkedService &&
                azureHDInsightActivity.linkedService instanceof HDInsightOnDemandLinkedService               
            ) {
                modelHDInsightActivity.typeProperties.clusterSettings = {
                    fileConnection: azureHDInsightActivity.resourceConnection
                        ? this._toLinkedServiceReferenceModel(azureHDInsightActivity.resourceConnection)
                        : undefined,
                    clusterType: azureHDInsightActivity.clusterType,
                    clusterSize: azureHDInsightActivity.clusterSize,
                    timeToLive: azureHDInsightActivity.timeToLive,
                    version: azureHDInsightActivity.version,
                    osType:
                        azureHDInsightActivity.clusterSshUserName || undefined
                            ? azureHDInsightActivity.osType
                            : undefined,
                    clusterNamePrefix: azureHDInsightActivity.clusterNamePrefix,
                    clusterUserName: azureHDInsightActivity.clusterUserName || undefined,
                    clusterPassword: passwordOrAKVtoSecretBase(azureHDInsightActivity.clusterPassword, this.context),
                    clusterSshUserName: azureHDInsightActivity.clusterSshUserName || undefined,
                    clusterSshPassword: passwordOrAKVtoSecretBase(
                        azureHDInsightActivity.clusterSshPassword,
                        this.context
                    ),
                    scriptActions:
                        !stringIsNullOrEmpty(azureHDInsightActivity.scriptName) &&
                            !stringIsNullOrEmpty(azureHDInsightActivity.scriptUri) &&
                            azureHDInsightActivity.scriptNodeTypes.length > 0
                            ? [
                                {
                                    name: azureHDInsightActivity.scriptName,
                                    uri: azureHDInsightActivity.scriptUri,
                                    parameters: azureHDInsightActivity.scriptParameters || undefined,
                                    roles: azureHDInsightActivity.scriptNodeTypes
                                }
                            ]
                            : undefined,
                    sparkVersion: azureHDInsightActivity.sparkVersion,
                    headNodeSize: azureHDInsightActivity.headNodeSize,
                    dataNodeSize: azureHDInsightActivity.dataNodeSize,
                    zookeeperNodeSize: azureHDInsightActivity.zookeeperNodeSize,
                    coreConfiguration: toParameterObject(azureHDInsightActivity.coreConfiguration),
                    hBaseConfiguration: toParameterObject(azureHDInsightActivity.hbaseConfiguration),
                    hdfsConfiguration: toParameterObject(azureHDInsightActivity.hdfsConfiguration),
                    hiveConfiguration: toParameterObject(azureHDInsightActivity.hiveConfiguration),
                    mapReduceConfiguration: toParameterObject(azureHDInsightActivity.mapReduceConfiguration),
                    oozieConfiguration: toParameterObject(azureHDInsightActivity.oozieConfiguration),
                    stormConfiguration: toParameterObject(azureHDInsightActivity.stormConfiguration),
                    yarnConfiguration: toParameterObject(azureHDInsightActivity.yarnConfiguration)
                };
                if (azureHDInsightActivity.hCatalogConnection) {
                    modelHDInsightActivity.typeProperties.hCatalogSettings = {
                        fileConnection: azureHDInsightActivity.hCatalogConnection
                            ? this._toLinkedServiceReferenceModel(azureHDInsightActivity.hCatalogConnection)
                            : undefined
                    };
                } else {
                    modelHDInsightActivity.typeProperties.hCatalogSettings = undefined;
                }
            } else {
                modelHDInsightActivity.typeProperties.clusterSettings = {
                    fileConnection: azureHDInsightActivity.resourceConnection
                        ? this._toLinkedServiceReferenceModel(azureHDInsightActivity.resourceConnection)
                        : undefined
                };
            }
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            // Overlay this on HDI activity
            _.merge(modelHDInsightActivity, parsed);
        }

        return modelHDInsightActivity;
    }

    protected async _toModel(modelActivity: Model.AzureHDInsightActivity): Promise<AzureHDInsightActivity> {
        const activity: AzureHDInsightActivity = AzureHDInsightActivity.MODEL.create<AzureHDInsightActivity>(
            modelActivity.name
        );
        const modelHDIActivity = modelActivity as Model.AzureHDInsightActivity;

        this._updatedCommonProperties(modelActivity, activity);
        ValueAdapter.toModel(
            activity,
            AzureHDInsightActivity.HDI_ACTIVITY_TYPE,
            modelHDIActivity.typeProperties.hdiActivityType
        );
        const hdiActivityType = modelHDIActivity.typeProperties.hdiActivityType;
        if (hdiActivityType === 'HDInsightHive' || hdiActivityType === 'HDInsightPig') {
            if (modelHDIActivity.typeProperties) {
                if (
                    modelHDIActivity.typeProperties.scriptSettings?.scriptPath &&
                    modelHDIActivity.typeProperties.scriptSettings?.scriptPath.type
                ) {
                    ValueAdapter.toModel(
                        activity,
                        AzureHDInsightActivity.SCRIPT_PATH,
                        modelHDIActivity.typeProperties.scriptSettings.scriptPath
                    );
                } else {
                    activity.scriptPath = new FilePath();
                    activity.scriptPath.path = modelHDIActivity.typeProperties.scriptSettings.scriptPath || '';
                }

                const args = toArgsString(
                    activity,
                    modelHDIActivity.typeProperties.arguments,
                    AzureHDInsightActivity.ARGUMENTS
                );
                if (args) {
                    activity.azureHDInsightActivityArguments = args;
                }
                activity.getDebugInfo = modelHDIActivity.typeProperties.getDebugInfo;
                activity.defines = toParameterClass(modelHDIActivity.typeProperties.defines, activity);
                activity.variables = toParameterClass(modelHDIActivity.typeProperties.variables, activity);
                if (hdiActivityType === 'HDInsightHive') {
                    activity.queryTimeout = modelHDIActivity.typeProperties.queryTimeout;
                }
                if (modelHDIActivity.typeProperties.scriptSettings.fileConnection !== undefined) {
                    const fileConnLs = getFabricLsForParameterizedConnections(
                        modelHDIActivity.typeProperties.scriptSettings.fileConnection,
                        ModelConstants.blobLinkedService,
                        activity);
                    activity.fileConnection = fileConnLs ? (fileConnLs as AzureBlobStorageLinkedService) :
                        ReferenceAdapter.toProxyModel<AzureBlobStorageLinkedService>(
                            Metamodel.modelFor(AzureBlobStorageLinkedService),
                            modelHDIActivity.typeProperties.scriptSettings.fileConnection,
                            this.context
                        );
                }
            }
        } else if (hdiActivityType === 'HDInsightMapReduce') {
            if (modelHDIActivity.typeProperties) {
                if (
                    modelHDIActivity.typeProperties.jarSettings?.jarFilePath &&
                    modelHDIActivity.typeProperties.jarSettings?.jarFilePath.type
                ) {
                    ValueAdapter.toModel(
                        activity,
                        AzureHDInsightActivity.JAR_FILE_PATH,
                        modelHDIActivity.typeProperties.jarSettings.jarFilePath
                    );
                } else {
                    activity.jarFilePath = new FilePath();
                    activity.jarFilePath.path = modelHDIActivity.typeProperties.jarSettings.jarFilePath || '';
                }

                const args = toArgsString(
                    activity,
                    modelHDIActivity.typeProperties.arguments,
                    AzureHDInsightActivity.ARGUMENTS
                );
                if (args) {
                    activity.azureHDInsightActivityArguments = args;
                }
                ValueAdapter.toModel(
                    activity,
                    AzureHDInsightActivity.CLASS_NAME,
                    modelHDIActivity.typeProperties.jarSettings.className
                );
                activity.getDebugInfo = modelHDIActivity.typeProperties.getDebugInfo;
                activity.defines = toParameterClass(modelHDIActivity.typeProperties.defines, activity);
                activity.jarLibs = modelHDIActivity.typeProperties.jarSettings.jarLibs || [];
                if (modelHDIActivity.typeProperties.jarSettings.fileConnection !== undefined) {
                    const fileConnLs = getFabricLsForParameterizedConnections(
                        modelHDIActivity.typeProperties.jarSettings.fileConnection,
                        ModelConstants.blobLinkedService,
                        activity);
                    activity.fileConnection = fileConnLs ? (fileConnLs as AzureBlobStorageLinkedService) :
                        ReferenceAdapter.toProxyModel<AzureBlobStorageLinkedService>(
                            Metamodel.modelFor(AzureBlobStorageLinkedService),
                            modelHDIActivity.typeProperties.jarSettings.fileConnection,
                            this.context
                        );
                }
            }
        } else if (hdiActivityType === 'HDInsightSpark') {
            activity.configType = 'Script';
            if (modelHDIActivity.typeProperties) {
                ValueAdapter.toModel(
                    activity,
                    AzureHDInsightActivity.ROOT_PATH,
                    modelHDIActivity.typeProperties.sparkSettings.rootPath
                );
                ValueAdapter.toModel(
                    activity,
                    AzureHDInsightActivity.ENTRY_FILE_PATH,
                    modelHDIActivity.typeProperties.sparkSettings.entryFilePath
                );
                const args = toArgsString(
                    activity,
                    modelHDIActivity.typeProperties.arguments,
                    AzureHDInsightActivity.ARGUMENTS
                );
                if (args) {
                    activity.azureHDInsightActivityArguments = args;
                }
                activity.className = modelHDIActivity.typeProperties.sparkSettings.className;
                activity.getDebugInfo = modelHDIActivity.typeProperties.getDebugInfo;
                activity.sparkConfig = toParameterClass(
                    modelHDIActivity.typeProperties.sparkSettings.sparkConfig,
                    activity
                );
                ValueAdapter.toModel(
                    activity,
                    AzureHDInsightActivity.PROXY_USER,
                    modelHDIActivity.typeProperties.sparkSettings.proxyUser
                );
                if (modelHDIActivity.typeProperties.sparkSettings.fileConnection !== undefined) {
                    const fileConnLs = getFabricLsForParameterizedConnections(
                        modelHDIActivity.typeProperties.sparkSettings.fileConnection,
                        ModelConstants.blobLinkedService,
                        activity);
                    activity.fileConnection = fileConnLs ? (fileConnLs as AzureBlobStorageLinkedService) :
                        ReferenceAdapter.toProxyModel<AzureBlobStorageLinkedService>(
                            Metamodel.modelFor(AzureBlobStorageLinkedService),
                            modelHDIActivity.typeProperties.sparkSettings.fileConnection,
                            this.context
                        );
                }
            }

            if (activity.className != null) {
                activity.configType = 'Jar';
            }
        } else if (hdiActivityType === 'HDInsightStreaming') {
            if (modelHDIActivity.typeProperties) {
                ValueAdapter.toModel(activity, AzureHDInsightActivity.MAPPER, modelHDIActivity.typeProperties.mapper);
                ValueAdapter.toModel(activity, AzureHDInsightActivity.REDUCER, modelHDIActivity.typeProperties.reducer);
                ValueAdapter.toModel(
                    activity,
                    AzureHDInsightActivity.INPUT,
                    modelHDIActivity.typeProperties.fileSettings.input
                );
                ValueAdapter.toModel(
                    activity,
                    AzureHDInsightActivity.OUTPUT,
                    modelHDIActivity.typeProperties.fileSettings.output
                );
                const args = toArgsString(
                    activity,
                    modelHDIActivity.typeProperties.arguments,
                    AzureHDInsightActivity.ARGUMENTS
                );
                if (args) {
                    activity.azureHDInsightActivityArguments = args;
                }
                activity.getDebugInfo = modelHDIActivity.typeProperties.getDebugInfo;
                activity.defines = toParameterClass(modelHDIActivity.typeProperties.defines, activity);

                if (modelHDIActivity.typeProperties.fileSettings.filePaths == null) {
                    activity.mapperFilePath = new FilePath();
                    activity.reducerFilePath = new FilePath();
                } else {
                    [activity.mapperFilePath.path, activity.reducerFilePath.path] =
                        modelHDIActivity.typeProperties.fileSettings.filePaths;
                    activity.mapperFilePath.path = activity.mapperFilePath.path || '';
                    activity.reducerFilePath.path = activity.reducerFilePath.path || '';
                }

                if (modelHDIActivity.typeProperties.fileSettings.fileConnection !== undefined) {
                    const fileConnLs = getFabricLsForParameterizedConnections(
                        modelHDIActivity.typeProperties.fileSettings.fileConnection,
                        ModelConstants.blobLinkedService,
                        activity);
                    activity.fileConnection = fileConnLs ? (fileConnLs as AzureBlobStorageLinkedService) :
                        ReferenceAdapter.toProxyModel<AzureBlobStorageLinkedService>(
                            Metamodel.modelFor(AzureBlobStorageLinkedService),
                            modelHDIActivity.typeProperties.fileSettings.fileConnection,
                            this.context
                        );
                }
            }
        }
        if (
            modelHDIActivity.typeProperties !== undefined &&
            modelHDIActivity.typeProperties.clusterSettings !== undefined
        ) {
            if (modelHDIActivity.typeProperties.clusterSettings.fileConnection !== undefined) {
                const resourceConnLs = getFabricLsForParameterizedConnections(
                    modelHDIActivity.typeProperties.clusterSettings.fileConnection,
                    ModelConstants.blobLinkedService,
                    activity
                );
                activity.resourceConnection = resourceConnLs ? (resourceConnLs as AzureBlobStorageLinkedService) :
                    ReferenceAdapter.toProxyModel<AzureBlobStorageLinkedService>(
                        Metamodel.modelFor(AzureBlobStorageLinkedService),
                        modelHDIActivity.typeProperties.clusterSettings.fileConnection,
                        this.context
                    );
            }
            activity.clusterType = modelHDIActivity.typeProperties.clusterSettings.clusterType || undefined;
            activity.version = modelHDIActivity.typeProperties.clusterSettings.version || undefined;
            activity.clusterSize = modelHDIActivity.typeProperties.clusterSettings.clusterSize || undefined;
            activity.timeToLive = modelHDIActivity.typeProperties.clusterSettings.timeToLive || undefined;
            activity.osType = modelHDIActivity.typeProperties.clusterSettings.osType || undefined;
            activity.clusterNamePrefix = modelHDIActivity.typeProperties.clusterSettings.clusterNamePrefix || undefined;
            activity.clusterUserName = modelHDIActivity.typeProperties.clusterSettings.clusterUserName || undefined;
            activity.clusterPassword = modelHDIActivity.typeProperties.clusterSettings.clusterPassword
                ? Constants.maskedCredential
                : undefined;
            activity.clusterSshUserName =
                modelHDIActivity.typeProperties.clusterSettings.clusterSshUserName || undefined;
            activity.clusterPassword = modelHDIActivity.typeProperties.clusterSettings.clusterPassword
                ? Constants.maskedCredential
                : undefined;
            if (
                modelHDIActivity.typeProperties.clusterSettings.scriptActions &&
                modelHDIActivity.typeProperties.clusterSettings.scriptActions.length > 0
            ) {
                const scriptAction = modelHDIActivity.typeProperties.clusterSettings.scriptActions[0];
                if (scriptAction != null && !stringIsNullOrEmpty(scriptAction.name)) {
                    activity.scriptName = scriptAction.name;
                    activity.scriptUri = scriptAction.uri;
                    activity.scriptNodeTypes = scriptAction.roles;
                    activity.scriptParameters = scriptAction.parameters;
                }
            }
            activity.sparkVersion = modelHDIActivity.typeProperties.clusterSettings.sparkVersion;
            activity.headNodeSize = modelHDIActivity.typeProperties.clusterSettings.headNodeSize;
            activity.dataNodeSize = modelHDIActivity.typeProperties.clusterSettings.dataNodeSize;
            activity.zookeeperNodeSize = modelHDIActivity.typeProperties.clusterSettings.zookeeperNodeSize;
            activity.coreConfiguration = toParameterClass(
                modelHDIActivity.typeProperties.clusterSettings.coreConfiguration,
                activity
            );
            activity.hbaseConfiguration = toParameterClass(
                modelHDIActivity.typeProperties.clusterSettings.hBaseConfiguration,
                activity
            );
            activity.hdfsConfiguration = toParameterClass(
                modelHDIActivity.typeProperties.clusterSettings.hdfsConfiguration,
                activity
            );
            activity.hiveConfiguration = toParameterClass(
                modelHDIActivity.typeProperties.clusterSettings.hiveConfiguration,
                activity
            );
            activity.mapReduceConfiguration = toParameterClass(
                modelHDIActivity.typeProperties.clusterSettings.mapReduceConfiguration,
                activity
            );
            activity.oozieConfiguration = toParameterClass(
                modelHDIActivity.typeProperties.clusterSettings.oozieConfiguration,
                activity
            );
            activity.stormConfiguration = toParameterClass(
                modelHDIActivity.typeProperties.clusterSettings.stormConfiguration,
                activity
            );
            activity.yarnConfiguration = toParameterClass(
                modelHDIActivity.typeProperties.clusterSettings.yarnConfiguration,
                activity
            );
        }
        if (modelHDIActivity.typeProperties.hCatalogSettings?.fileConnection !== undefined) {
            const hCatalogConnLs = getFabricLsForParameterizedConnections(
                modelHDIActivity.typeProperties.hCatalogSettings.fileConnection,
                ModelConstants.azureSqlDatabase,
                activity
            );
            activity.hCatalogConnection = hCatalogConnLs ? (hCatalogConnLs as AzureSqlDatabaseLinkedService) :
                ReferenceAdapter.toProxyModel<AzureSqlDatabaseLinkedService>(
                    Metamodel.modelFor(AzureSqlDatabaseLinkedService),
                    modelHDIActivity.typeProperties.hCatalogSettings.fileConnection,
                    this.context
                );
        }
        if (modelHDIActivity.linkedServiceName !== undefined) {
            const lsType = modelHDIActivity.linkedServiceName.referenceType ?? ModelConstants.hdiOnDemandLinkedService;
            activity.linkedServiceType = lsType;
            const linkedServiceConnLs = getFabricLsForParameterizedConnections(
                modelHDIActivity.linkedServiceName,
                lsType,
                activity
            );
            activity.linkedService = linkedServiceConnLs ? linkedServiceConnLs : ReferenceAdapter.toProxyModel<LinkedService>(
                Metamodel.modelFor(HDInsightLinkedService),
                modelHDIActivity.linkedServiceName,
                this.context
            );
        }

        activity.raw = JSON.stringify(
            await ValueAdapter.remainder(this.context, modelHDIActivity, AzureHDInsightActivity.MODEL)
        );

        return activity;
    }
}
